import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useHistory } from "react-router-dom";
import { SignType } from "../../domain";
import { useGtag, useJsonLd } from "../../hooks";
import useSuppressChat from "../../hooks/useSuppressChat";
import { useEmailModal } from "../../services/EmailModal";
import { captureWithShop } from "../../services/posthogCapture";
import {
  ADDON_TOGGLED,
  PRODUCT_VIEWED,
  RECOMMENDATION_CLICKED,
  VARIANT_SELECTED,
} from "../../services/posthogEvents.js";
import { usd } from "../../util/formatCurrency.js";
import AddonSelector from "./components/AddonSelector/AddonSelector.jsx";
import FavouriteButton from "./components/FavouriteButton.jsx";
import {
  BlockIcon,
  HomeIcon,
  InfoIcon,
  LockIcon,
  PadlockIcon,
  TimerIcon,
} from "./components/Icons.jsx";
import s from "./components/ProductPage.module.css";
import AffirmBadge from "./components/ProductPage/AffirmBadge";
import {
  cartLabelFor,
  getBestVariant,
  getFulfillmentSpeed,
  getProductId,
  getStockIndicator,
  isAnySendin,
  isVaulted as isProductVaulted,
  isSoldOut,
  shippingTextFor,
} from "./components/ProductPage/productAdapter";
import ProductForm from "./components/ProductPage/ProductForm";
import ProductHeader from "./components/ProductPage/ProductHeader";
import ProductImage from "./components/ProductPage/ProductImage";
import ms from "./components/ProductPage/ProductPage.module.css";
import SendinGuidelines from "./components/ProductPage/SendinGuidelines";
import ShareButton from "./components/ProductPage/ShareButton";
import {
  ProductPageContext,
  addService,
  createInitialState,
  dismissCoa,
  reducer,
  removeService,
  selectVariant,
  syncCoa,
  updateAddons,
  updateServiceText,
} from "./components/ProductPage/state";
import ServiceSelector from "./components/ServiceSelector/ServiceSelector";
import VariantPicker from "./components/ProductPage/VariantPicker";
import WaitlistForm from "./components/ProductPage/WaitlistForm";
import YouMayAlsoLike from "./components/YouMayAlsoLike";
import CanonicalUrl from "../../components/CanonicalUrl";
import { useAddToCart } from "./hooks/useAddToCart";
import { productAvailability } from "./utils.js";
import { Button, Skeleton } from "../../themeV2";

const AFFIRM_MINIMUM_CENTS = 50000;

const noop = (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
};

function ProductPageSkeleton() {
  return (
    <div className={s.page}>
      {/* Breadcrumb */}
      <nav className={s.breadcrumb} aria-hidden="true">
        <span className={s.breadcrumbLink}>
          <Skeleton
            variant="circle"
            style={{ width: "1.5rem", height: "1.5rem" }}
          />
          <Skeleton variant="text" style={{ width: 80 }} />
        </span>
        <span aria-hidden="true">/</span>
        <Skeleton variant="text" style={{ width: 120 }} />
      </nav>

      <div className={s.grid}>
        {/* Left column */}
        <div className={s.stickyCol}>
          <Skeleton
            variant="rect"
            style={{
              width: "100%",
              aspectRatio: "1",
              maxHeight: "40vh",
              borderRadius: "var(--shape-md)",
            }}
          />
          <div className={s.variantDesktop}>
            <Skeleton
              variant="rect"
              style={{
                width: "100%",
                height: 56,
                borderRadius: "var(--shape-sm)",
              }}
            />
          </div>
        </div>

        {/* Right column */}
        <div className={s.detailsCol}>
          {/* Name + price */}
          <div className={s.aboveFoldHeader}>
            <Skeleton variant="text" size="xl" style={{ width: "60%" }} />
            <Skeleton variant="text" size="xl" style={{ width: 72 }} />
          </div>

          {/* Product header lines */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skeleton variant="text" style={{ width: "40%" }} />
            <Skeleton variant="text" style={{ width: "70%" }} />
            <Skeleton variant="text" style={{ width: "55%" }} />
            <Skeleton variant="text" style={{ width: "85%" }} />
          </div>

          {/* Mobile variant picker */}
          <div className={s.variantMobile}>
            <Skeleton
              variant="rect"
              style={{
                width: "100%",
                height: 56,
                borderRadius: "var(--shape-sm)",
              }}
            />
          </div>

          {/* Form area */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Skeleton variant="text" style={{ width: 100 }} />
            <Skeleton
              variant="rect"
              style={{
                width: "100%",
                height: 44,
                borderRadius: "var(--shape-sm)",
              }}
            />
            <Skeleton
              variant="rect"
              style={{
                width: "100%",
                height: 44,
                borderRadius: "var(--shape-sm)",
              }}
            />
          </div>

          {/* Trust signals */}
          <div className={s.desktopActions}>
            <Skeleton
              variant="text"
              style={{ width: 160, alignSelf: "center" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductMetaData({ product, influencer, route }) {
  const description =
    product.subtext ||
    `Autographed ${product.name} signed by ${influencer.name}`;
  const canonicalUrl = `https://streamily.com/${route}/${product.product_id}`;
  const image = product.image_url || influencer.avatar_url;

  const schema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description,
      url: canonicalUrl,
      image: product.image_url || undefined,
      offers: {
        "@type": "Offer",
        price: ((product.price + (product.product_bump || 0)) / 100).toFixed(2),
        priceCurrency: "USD",
        availability: productAvailability(product),
      },
    }),
    [product, description, canonicalUrl],
  );

  useJsonLd(schema);

  return (
    <>
      <Helmet>
        <title>{`${product.name} | Signed by ${influencer.name} | Streamily`}</title>
        <meta name="description" content={description} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta
          property="og:title"
          content={`${product.name} | Signed by ${influencer.name} | Streamily`}
        />
        <meta property="og:description" content={description} />
        {image && <meta property="og:image" content={image} />}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Streamily" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${product.name} | Signed by ${influencer.name} | Streamily`}
        />
        <meta name="twitter:description" content={description} />
        {image && <meta name="twitter:image" content={image} />}
      </Helmet>
      <CanonicalUrl href={canonicalUrl} />
    </>
  );
}

ProductMetaData.propTypes = {
  product: PropTypes.object.isRequired,
  influencer: PropTypes.object.isRequired,
  route: PropTypes.string.isRequired,
};

// eslint-disable-next-line complexity
export default function ProductPage({
  route,
  productId,
  influencer,
  allProducts,
  customImageProducts,
  isLoading: isShopLoading,
  breadcrumb,
}) {
  const history = useHistory();
  const { block, unblock } = useEmailModal();
  const gtag = useGtag();

  // The chat bubble covers the Add to Cart button.
  useSuppressChat(true);

  // Find the product from allProducts + customImageProducts
  const pid = Number.parseInt(productId, 10);
  const combinedProducts = useMemo(
    () => [...(allProducts || []), ...(customImageProducts || [])],
    [allProducts, customImageProducts],
  );
  const product = useMemo(() => {
    if (!combinedProducts.length || !Number.isInteger(pid)) return null;

    // Direct match
    const direct = combinedProducts.find((p) => p.product_id === pid);
    if (direct) return direct;

    // Variant match: search inside each product's _variants
    const variantMatch = combinedProducts.find(
      (p) =>
        Array.isArray(p._variants) &&
        p._variants.some((v) => v.product_id === pid),
    );
    if (variantMatch) return variantMatch;

    return null;
  }, [combinedProducts, pid]);

  // Find if we should pre-select a specific variant
  const initialVariant = useMemo(() => {
    if (!product || !Array.isArray(product._variants)) return null;
    // If the pid matches the parent product, no specific variant pre-selection
    if (product.product_id === pid) return null;
    // The pid matched a variant
    return product._variants.find((v) => v.product_id === pid) || null;
  }, [product, pid]);

  const [state, dispatch] = useReducer(reducer, product, (prod) => {
    const initial = createInitialState(prod, influencer);
    // Pre-select variant if URL points to a specific variant
    if (initialVariant) {
      return { ...initial, selectedVariant: initialVariant };
    }
    return initial;
  });

  const contextValue = useMemo(() => [state, dispatch], [state, dispatch]);

  // Sync coaDismissed once when influencer data first arrives.
  // The reducer initializer defaults coaDismissed=true when influencer is
  // absent, suppressing the COA auto-add until we know the real preference.
  // Uses a ref so SWR revalidations don't undo a user's manual dismissal.
  const coaSyncedForId = useRef(influencer?.influencer_id ?? null);
  useEffect(() => {
    if (!influencer) return;
    if (coaSyncedForId.current === influencer.influencer_id) return;
    coaSyncedForId.current = influencer.influencer_id;
    dispatch(syncCoa(influencer.coa_opt_in));
  }, [influencer]);

  // When product data arrives after initial load, select the correct variant
  useEffect(() => {
    if (!product || state.selectedVariant) return;
    if (initialVariant) {
      dispatch(selectVariant(initialVariant));
    } else if (product._variants?.length >= 1) {
      dispatch(selectVariant(getBestVariant(product)));
    }
  }, [product, initialVariant, state.selectedVariant]);

  // Email modal blocking
  useEffect(() => {
    block();
    return () => unblock();
  }, [block, unblock]);

  // URL update when variant selected
  useEffect(() => {
    if (!state.selectedVariant || !route) return;
    const path = `/${route}/${state.selectedVariant.product_id}`;
    history.replace(path);
  }, [state.selectedVariant, route, history]);

  // Tracking on mount
  useEffect(() => {
    if (!product || !influencer) return;
    captureWithShop(
      PRODUCT_VIEWED,
      {
        shop_id: influencer.influencer_id ?? null,
        shop_route: influencer.route ?? route,
        shop_name: influencer.name ?? null,
        product_id: getProductId(product),
        product_name: product.name,
        product_price: product._totalPrice,
      },
      influencer.influencer_id,
    );
  }, [product, influencer, route]);

  const handleVariantSelect = useCallback(
    (variant) => {
      dispatch(selectVariant(variant));
      captureWithShop(
        VARIANT_SELECTED,
        { product_id: variant.product_id, shop_route: route },
        influencer?.influencer_id,
      );
      gtag("event", "select_item", {
        items: [{ item_id: variant.product_id }],
      });
    },
    [route, gtag, influencer?.influencer_id],
  );

  const {
    addToCart,
    isAddingToCart,
    errorMessage,
    resolved,
    resolvedProductId,
    soldOut,
    cartDisabled,
    cartTotal,
  } = useAddToCart({ product, state, influencer, route });

  // Sold out and vaulted are checked on the resolved (variant-level) product,
  // not the parent, because individual variants carry their own stock.
  const resolvedSoldOut = resolved ? isSoldOut(resolved) : false;
  const isVaulted = resolved ? isProductVaulted(resolved) : false;
  const unavailable = resolvedSoldOut || isVaulted;

  const stockIndicator = getStockIndicator(resolved?.stock ?? product?.stock);
  const fulfillmentSpeed = getFulfillmentSpeed(product);
  const name = resolved ? resolved.name : product?.name;
  const description = resolved ? resolved.subtext : product?.subtext;

  const handleRecommendationClick = useCallback(
    (p) => {
      const targetId = p.product_id;
      captureWithShop(
        RECOMMENDATION_CLICKED,
        {
          from_product_id: getProductId(product),
          to_product_id: targetId,
          shop_route: route,
        },
        influencer?.influencer_id,
      );
      gtag("event", "select_item", {
        items: [{ item_id: targetId }],
        item_list_name: "recommendations",
      });
      history.push(`/${route}/${targetId}`);
    },
    [history, route, product, gtag, influencer?.influencer_id],
  );

  if (isShopLoading) {
    return <ProductPageSkeleton />;
  }

  if (!product) {
    return (
      <div className={s.page}>
        <div className={ms.notFound}>
          <h2 className={ms.notFoundTitle}>Product not found</h2>
          <p className={ms.notFoundText}>
            This product may have been removed or is no longer available.
          </p>
          <Button
            as={Link}
            to={`/${route}`}
            size="sm"
            className={ms.notFoundLink}
          >
            Back to shop
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ProductPageContext.Provider value={contextValue}>
      {product && influencer && (
        <ProductMetaData
          product={product}
          influencer={influencer}
          route={route}
        />
      )}
      <div className={s.page}>
        {/* Breadcrumb with shop avatar + mobile-only fav/share */}
        <div className={s.breadcrumbRow}>
          <nav aria-label="Breadcrumb" className={s.breadcrumb}>
            <Link
              to={breadcrumb?.to ?? `/${route}`}
              className={s.breadcrumbLink}
            >
              {(breadcrumb?.avatar ?? influencer?.avatar_url) && (
                <img
                  className={s.breadcrumbAvatar}
                  src={breadcrumb?.avatar ?? influencer.avatar_url}
                  alt=""
                  aria-hidden="true"
                />
              )}
              {breadcrumb?.label ?? influencer?.name ?? "Shop"}
            </Link>
            <span aria-hidden="true">/</span>
            <span className={s.breadcrumbCurrent} aria-current="page">
              {name}
            </span>
          </nav>
          <div className={s.breadcrumbActions}>
            <FavouriteButton variant="dark" productId={getProductId(product)} />
            <ShareButton
              productName={name}
              url={`${window.location.origin}/${route}/${resolvedProductId}`}
            />
          </div>
        </div>

        {/* Two-column grid */}
        <form onSubmit={unavailable ? noop : errorMessage ? noop : addToCart}>
          <div className={s.grid}>
            {/* Left column - sticky on desktop with image + variant picker */}
            <div className={s.stickyCol}>
              <ProductImage
                product={resolved || product}
                customerImage={state.customerImage}
                signedType={
                  state.selectedVariant?.signed_type ?? product.signed_type
                }
                soldOut={resolvedSoldOut}
                isVaulted={isVaulted}
              />

              {/* Desktop-only variant picker */}
              <div className={s.variantDesktop}>
                <VariantPicker
                  variants={product._variants}
                  selectedVariant={state.selectedVariant}
                  onSelect={handleVariantSelect}
                />
              </div>
            </div>

            {/* Right column - details flow */}
            <div className={s.detailsCol}>
              {/* 1. Above-fold: name + favourite + price */}
              <div>
                <div className={s.aboveFoldHeader}>
                  <h2 className={ms.productName}>{name}</h2>
                  <span
                    className={
                      isVaulted
                        ? ms.productPriceVaulted
                        : resolvedSoldOut
                        ? ms.productPriceSoldOut
                        : ms.productPrice
                    }
                  >
                    {usd(resolved?._totalPrice ?? product._totalPrice)}
                    {influencer?.show_price_disclaimer && (
                      <button
                        type="button"
                        className={ms.priceDisclaimer}
                        title="Price includes platform fee"
                        aria-label="Price includes platform fee"
                      >
                        <InfoIcon className={ms.priceDisclaimerIcon} />
                      </button>
                    )}
                  </span>
                  {!unavailable && stockIndicator && (
                    <span
                      className={`${
                        stockIndicator.tier === "critical"
                          ? s.stockCritical
                          : s.stockLow
                      }`}
                      role="status"
                    >
                      {stockIndicator.text}
                    </span>
                  )}
                  <div className={ms.headerActions}>
                    <FavouriteButton
                      variant="dark"
                      productId={getProductId(product)}
                    />
                    <ShareButton
                      productName={name}
                      url={`${window.location.origin}/${route}/${resolvedProductId}`}
                    />
                  </div>
                </div>

                {!unavailable &&
                  (resolved?._totalPrice ?? product._totalPrice) +
                    (resolved?.product_bump ?? product.product_bump ?? 0) >
                    AFFIRM_MINIMUM_CENTS && <AffirmBadge />}

                {/* 2. Product details (signers, type, desc) */}
                <ProductHeader
                  product={product}
                  resolvedDescription={description}
                  selectedVariant={state.selectedVariant}
                />

                {isAnySendin(product) && (
                  <SendinGuidelines
                    influencerId={
                      influencer?.influencer_id ?? product.influencer_id
                    }
                  />
                )}
              </div>

              {/* 3. Choose signing option — mobile only */}
              <div className={s.variantMobile}>
                <VariantPicker
                  variants={product._variants}
                  selectedVariant={state.selectedVariant}
                  onSelect={handleVariantSelect}
                />
              </div>

              {/* Status banner + waitlist for unavailable products */}
              {unavailable && (
                <div
                  className={
                    resolvedSoldOut
                      ? ms.statusBannerSoldOut
                      : ms.statusBannerVaulted
                  }
                  role="status"
                >
                  <div className={ms.statusBannerHeader}>
                    {resolvedSoldOut ? (
                      <BlockIcon className={ms.statusBannerIcon} />
                    ) : (
                      <LockIcon className={ms.statusBannerIcon} />
                    )}
                    <span className={ms.statusBannerTitle}>
                      {resolvedSoldOut
                        ? "This signing option is sold out"
                        : "This product is in the vault"}
                    </span>
                  </div>
                  <WaitlistForm
                    productId={resolvedProductId}
                    isVaulted={isVaulted}
                  />
                </div>
              )}

              {/* Purchase flow — only when available */}
              {!unavailable && (
                <>
                  {/* 4. Personalization & details */}
                  <ProductForm product={resolved} />

                  {/* 5. Services (quote, doodle, etc.) */}
                  <ServiceSelector
                    productId={resolvedProductId}
                    services={state.services}
                    onAdd={(svc) => dispatch(addService(svc))}
                    onRemove={(id) => dispatch(removeService(id))}
                    onTextChange={(id, text) =>
                      dispatch(updateServiceText(id, text))
                    }
                  />

                  {/* 6. Add-ons (physical + experience) */}
                  <AddonSelector
                    productId={resolvedProductId}
                    selectedAddons={state.addons}
                    onAddonsChange={(val) => dispatch(updateAddons(val))}
                    coaDismissed={state.coaDismissed}
                    onCoaDismiss={() => dispatch(dismissCoa())}
                    onTrack={(addon, action, pid) => {
                      captureWithShop(
                        ADDON_TOGGLED,
                        {
                          addon_name: addon.name,
                          action,
                          product_id: pid,
                        },
                        influencer?.influencer_id,
                      );
                      gtag(
                        "event",
                        action === "remove"
                          ? "remove_from_cart"
                          : "add_to_cart",
                        {
                          items: [{ item_id: pid, item_name: addon.name }],
                        },
                      );
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Sticky bottom CTA — only when available */}
          {!unavailable && (
            <div className={s.stickyCta}>
              <div className={s.stickyCtaInner}>
                {/* Desktop product summary */}
                <div className={s.stickySummary}>
                  <img
                    className={s.stickySummaryThumb}
                    src={resolved?.image_url ?? product.image_url}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className={s.stickySummaryText}>
                    <div className={s.stickySummaryTextHeader}>
                      <span className={s.stickySummaryName}>
                        {product.name}
                      </span>
                      {resolved?.signed_type && (
                        <span className={s.stickySummaryVariant}>
                          {" "}
                          {SignType.toLabel(resolved.signed_type)}
                        </span>
                      )}
                    </div>
                    {fulfillmentSpeed && (
                      <span
                        className={
                          fulfillmentSpeed.speed === "fast"
                            ? s.stickySummarySpeedFast
                            : fulfillmentSpeed.speed === "moderate"
                            ? s.stickySummarySpeedModerate
                            : s.stickySummarySpeedSlow
                        }
                        title={fulfillmentSpeed.tooltip}
                      >
                        <TimerIcon className={s.speedIcon} />
                        {fulfillmentSpeed.text}
                      </span>
                    )}
                  </div>
                </div>

                <div className={s.stickyCtaActions}>
                  <Button
                    type="submit"
                    size="md"
                    className={s.stickyCtaBtn}
                    disabled={cartDisabled}
                    loading={isAddingToCart}
                  >
                    {cartLabelFor({ isAddingToCart, soldOut, cartTotal })}
                  </Button>
                  <div className={s.trustSignalsDesktop}>
                    <span className={s.trustSignal}>
                      <HomeIcon className={s.trustSignalIcon} />
                      {shippingTextFor(resolved)}
                    </span>

                    <span className={s.trustSignal}>
                      <PadlockIcon className={s.trustSignalIcon} />
                      Secure checkout
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        <br />
        <br />

        {/* Recommendations */}
        <YouMayAlsoLike
          allProducts={combinedProducts}
          currentProduct={product}
          route={route}
          talentName={influencer?.name}
          onProductClick={handleRecommendationClick}
        />
      </div>
    </ProductPageContext.Provider>
  );
}

ProductPage.propTypes = {
  route: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  influencer: PropTypes.object,
  allProducts: PropTypes.array,
  customImageProducts: PropTypes.array,
  isLoading: PropTypes.bool,
  breadcrumb: PropTypes.shape({
    to: PropTypes.string,
    label: PropTypes.string,
    avatar: PropTypes.string,
  }),
};
